import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactElement } from 'react'
import { DayPicker, type DayPickerProps } from 'react-day-picker'

import { cn } from '@/lib/cn'

/**
 * Calendário do design system. O `<input type="date">` nativo é proibido no
 * projeto: varia entre navegadores, ignora o locale e não combina com o resto.
 */
/**
 * O `locale` traduz as DATAS, mas não os rótulos de acessibilidade: o
 * `react-day-picker` monta `labelDayButton` com o prefixo "Today," e a navegação
 * com "Go to the Previous Month" — strings inglesas fixas no pacote. Num app
 * pt-BR isso chega ao leitor de tela em inglês, então traduzimos aqui.
 */
const LABELS: DayPickerProps['labels'] = {
  labelDayButton: (date, modifiers) => {
    const data = format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })
    if (modifiers.today) return `Hoje, ${data}`
    return data
  },
  labelPrevious: () => 'Ir para o mês anterior',
  labelNext: () => 'Ir para o próximo mês',
}

export function Calendar({ className, classNames, ...props }: DayPickerProps): ReactElement {
  return (
    <DayPicker
      locale={ptBR}
      labels={LABELS}
      showOutsideDays
      className={cn('relative p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'space-y-3',
        month_caption: 'flex justify-center pt-1 items-center h-7',
        caption_label: 'text-sm font-medium capitalize',
        // Barra de navegação ancorada no root (relative acima): as setas flanqueiam
        // o cabeçalho. `pointer-events-none` na barra + `auto` nos botões deixa o
        // clique passar pro calendário fora das setas, sem a barra cobrir os dias.
        nav: 'pointer-events-none absolute inset-x-3 top-3 flex items-center justify-between',
        button_previous:
          'pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40',
        button_next:
          'pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-9 text-[11px] font-normal capitalize',
        week: 'flex w-full mt-1',
        day: 'h-9 w-9 p-0 text-center text-sm',
        day_button:
          'h-9 w-9 rounded-md font-normal hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected: 'bg-primary text-primary-foreground hover:bg-primary',
        range_middle: 'bg-accent text-accent-foreground',
        today: 'font-semibold text-primary',
        outside: 'text-muted-foreground/40',
        disabled: 'text-muted-foreground/30',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          ),
      }}
      {...props}
    />
  )
}
